import React, { useCallback, useEffect } from 'react';
import SpinnerLoading from 'src/components/_common/Loading/SpinnerLoading';
import { addDatadogError } from 'src/libs/datadog';

import { trackEvent } from '@/libs/analytics/events';
import type { ViewDigitalCardEvent } from '@/libs/analytics/types/card';

import DigitalCardLayer from './_components/DigitalCardLayer';
import { useDigitalCard } from './_hooks/useDigitalCard';
import type { DigitalCardConfig } from './context';
import { DEFAULT_MAX_WIDTH, DigitalCardProvider } from './context';
import DigitalCardStyled from './index.styled';

interface DigitalCardContentProps {
  trackingParams?: ViewDigitalCardEvent['params'];
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  onError?: () => void;
}

type DigitalCardProps = DigitalCardConfig & DigitalCardContentProps;

/**
 * [디지털 카드 실제 렌더링 컴포넌트 (Provider 내부)]
 *
 * @description
 * 디지털 카드의 실제 렌더링을 담당하는 핵심 컴포넌트입니다.
 * DigitalCardProvider 내부에서 사용되며, 카드의 상태 관리와 렌더링을 처리합니다.
 *
 * @param props
 * ```ts
 * {
 *   loadingComponent?: React.ReactNode; // 로딩 컴포넌트 (이미지 로딩 중 표시될 컴포넌트)
 *   errorComponent?: React.ReactNode; // 에러 컴포넌트 (이미지 로딩 실패 시 표시될 컴포넌트)
 *   onError?: () => void; // 이미지 로딩 실패 시 호출될 함수
 * }
 * ```
 *
 * @structure
 * ```
 * DigitalCard
 * ├── CardFace[]           # 카드의 앞/뒷면 배열
 * │   ├── CardLayer[]      # 각 면의 레이어 배열
 * │   │   ├── Image        # 이미지 레이어
 * │   │   └── Component    # 컴포넌트 레이어
 * │   └── ...
 * └── ...
 * ```
 *
 * @features
 * - 이미지 프리로딩 및 로딩 상태 관리
 * - 카드 플립 애니메이션 처리
 * - 터치/클릭 이벤트 핸들링
 * - 레이어 기반 렌더링 시스템
 *
 * @see {@link CardLayer} 카드 레이어 컴포넌트
 */
const DigitalCardContent = ({ trackingParams, loadingComponent, errorComponent, onError }: DigitalCardContentProps) => {
  const {
    config: { width, height, flip, tilt, maxWidth },
    state: { cardRef, isInteracting, isAllResourcesLoaded, isAllResourceLoadError, visibleFaces },
    actions: { handleFlipToNext, onMouseEnter, onMouseLeave, onMouseMove, onTouchStart, onTouchMove, onTouchEnd },
  } = useDigitalCard();

  const tiltHandlers = { onMouseEnter, onMouseLeave, onMouseMove, onTouchStart, onTouchMove, onTouchEnd };

  useEffect(() => {
    trackEvent({ name: 'view_digital_card', params: trackingParams ?? {} });
  }, []);

  const handleFlipWithTracking = useCallback(() => {
    trackEvent({
      name: 'view_card_back',
      params: {
        card_name: trackingParams?.card_name,
        card_idx: trackingParams?.card_idx,
      },
    });
    handleFlipToNext();
  }, [handleFlipToNext, trackingParams]);

  useEffect(() => {
    if (isAllResourceLoadError) {
      const error = new Error('이미지 로딩에 실패하였습니다.');
      addDatadogError(error);
      onError?.();
    }
  }, [isAllResourceLoadError]);

  return (
    <DigitalCardStyled.Container $width={width} $height={height} $maxWidth={maxWidth ?? DEFAULT_MAX_WIDTH}>
      {!isAllResourceLoadError && (
        <DigitalCardStyled.Wrapper
          ref={cardRef}
          $width={width}
          $height={height}
          $maxWidth={maxWidth ?? DEFAULT_MAX_WIDTH}
          $isAllImagesLoaded={isAllResourcesLoaded}
          $tilt={tilt ?? false}
          onClick={flip ? handleFlipWithTracking : undefined}
          {...tiltHandlers}
          aria-label="디지털 카드"
        >
          {visibleFaces.map(({ face, position }) => (
            <DigitalCardStyled.DigitalCardFace key={face.idx} $isInteracting={isInteracting} $position={position}>
              {face.layers?.map((layer) => (
                <DigitalCardLayer.render
                  key={layer.idx}
                  faceIdx={face.idx}
                  borderRadius={face.borderRadius}
                  {...layer}
                />
              ))}
            </DigitalCardStyled.DigitalCardFace>
          ))}
        </DigitalCardStyled.Wrapper>
      )}

      {/* 이미지 로딩 완료 전 로딩 스피너 표시 */}
      {!isAllResourcesLoaded && !isAllResourceLoadError && (
        <DigitalCardStyled.LoadingContainer>
          {(loadingComponent as React.ReactNode) ?? <SpinnerLoading size={24} />}
        </DigitalCardStyled.LoadingContainer>
      )}

      {/* 이미지 로딩 실패 시 컴포넌트 표시 */}
      {isAllResourceLoadError && (
        <DigitalCardStyled.ErrorContainer>{(errorComponent as React.ReactNode) ?? '이미지 로딩 실패'}</DigitalCardStyled.ErrorContainer>
      )}
    </DigitalCardStyled.Container>
  );
};

/**
 * [디지털 카드 컴포넌트]
 *
 * @description
 * 디지털 카드를 표현하기 위한 컴포넌트 시스템입니다.
 * 기본 사용과 고급 사용 두 가지 방식을 제공합니다.
 *
 * @param props
 * ```ts
 * {
 *   faces: DigitalCardFace[]; // 카드의 각 면 정보 배열
 *   width: number; // 카드 너비
 *   height: number; // 카드 높이
 *   flip?: boolean; // 플립 기능 활성화 여부
 *   tilt?: boolean; // 틸트 기능 활성화 여부
 *   loadingComponent?: React.ReactNode; // 로딩 컴포넌트 (이미지 로딩 중 표시될 컴포넌트)
 *   errorComponent?: React.ReactNode; // 에러 컴포넌트 (이미지 로딩 실패 시 표시될 컴포넌트)
 *   onError?: () => void; // 이미지 로딩 실패 시 호출될 함수
 * }
 * ```
 *
 * @usage
 *
 * 기본 사용:
 * ```tsx
 * <DigitalCard
 *   faces={cardFaces}
 *   options={cardOptions}
 * />
 * ```
 *
 * 고급 사용:
 * ```tsx
 * <DigitalCard.Provider options={providerOptions}>
 *   <DigitalCard.Content/>
 *
 *   // 커스텀 컴포넌트와 상태 공유
 *   <CustomComponent />
 * </DigitalCard.Provider>
 * ```
 *
 * @components
 * - {@link DigitalCard} - 단일 컴포넌트 방식
 * - {@link DigitalCard.Provider} - 카드 상태 및 컨텍스트 제공
 * - {@link DigitalCard.Content} - 실제 렌더링 컴포넌트
 *
 * @hooks
 * - `useDigitalCardConfig()` - 카드 설정값 접근
 *   ```ts
 *   const {
 *     faces,          // 카드의 각 면 정보 배열
 *     width,          // 카드 너비
 *     height,         // 카드 높이
 *     flip,           // 플립 기능 활성화 여부
 *     tilt,           // 틸트 기능 활성화 여부
 *     imageLoadRetryCount, // 이미지 로딩 재시도 횟수
 *     imageLoadDelayTime, // 이미지 로딩 재시도 딜레이 시간
 *     spriteImageLoadRetryCount, // 스프라이트 이미지 로딩 재시도 횟수
 *     spriteImageLoadDelayTime, // 스프라이트 이미지 로딩 재시도 딜레이 시간
 *   } = useDigitalCardConfig();
 *   ```
 *
 * - `useDigitalCardState()` - 카드 상태 접근
 *   ```ts
 *   const {
 *     currentFaceIdx,     // 현재 보여지는 면의 인덱스
 *     isInteracting,      // 사용자 인터랙션 진행 중 여부
 *     cardRef,            // 카드 DOM 요소 참조
 *     visibleFaces,       // 현재 보이는 면들 (이전/현재/다음)
 *     isAllImagesLoaded,  // 모든 이미지 로딩 완료 여부
 *     completedImages     // 로딩 완료된 이미지 목록
 *   } = useDigitalCardState();
 *   ```
 *
 * - `useDigitalCardActions()` - 카드 액션 접근
 *   ```ts
 *   const {
 *     // 카드 제어
 *     handleFlipToNext,     // 다음 면으로 플립
 *     handleFlipToPrev,     // 이전 면으로 플립
 *
 *     // 마우스/터치 이벤트
 *     onMouseEnter,         // 마우스 진입 핸들러
 *     onMouseLeave,         // 마우스 이탈 핸들러
 *     onMouseMove,          // 마우스 이동 핸들러
 *     onTouchStart,         // 터치 시작 핸들러
 *     onTouchMove,          // 터치 이동 핸들러
 *     onTouchEnd,           // 터치 종료 핸들러
 *
 *     // 이미지 로딩
 *     handleImageLoadComplete // 이미지 로딩 완료 시 작동 핸들러
 *     handleImageLoadError    // 이미지 로딩 실패 시 작동 핸들러
 *   } = useDigitalCardActions();
 *   ```
 *
 * - `useDigitalCard()` - 모든 Context 한 번에 접근
 *   ```ts
 *   const {
 *     config,   // useDigitalCardConfig()의 모든 반환값
 *     state,    // useDigitalCardState()의 모든 반환값
 *     actions   // useDigitalCardActions()의 모든 반환값
 *   } = useDigitalCard();
 *   ```
 *
 * @see {@link DigitalCardProvider} 상태 제공자
 * @see {@link useDigitalCardState} 카드 상태 관리 훅
 * @see {@link useDigitalCardConfig} 카드 설정 관리 훅
 * @see {@link useDigitalCardActions} 카드 액션 관리 훅
 */
const DigitalCard = Object.assign(
  ({
    faces,
    width,
    height,
    flip,
    tilt,
    maxWidth,
    imageLoadRetryCount,
    imageLoadDelayTime,
    videoLoadRetryCount,
    videoLoadDelayTime,
    spriteImageLoadRetryCount,
    spriteImageLoadDelayTime,
    trackingParams,
    loadingComponent,
    errorComponent,
    onError,
  }: DigitalCardProps) => {
    if (!Array.isArray(faces) || faces.length === 0) {
      onError?.();
      return null;
    }

    return (
      <DigitalCardProvider
        faces={faces}
        width={width}
        height={height}
        flip={flip}
        tilt={tilt}
        maxWidth={maxWidth}
        imageLoadRetryCount={imageLoadRetryCount}
        imageLoadDelayTime={imageLoadDelayTime}
        videoLoadRetryCount={videoLoadRetryCount}
        videoLoadDelayTime={videoLoadDelayTime}
        spriteImageLoadRetryCount={spriteImageLoadRetryCount}
        spriteImageLoadDelayTime={spriteImageLoadDelayTime}
      >
        <DigitalCardContent trackingParams={trackingParams} loadingComponent={loadingComponent} errorComponent={errorComponent} onError={onError} />
      </DigitalCardProvider>
    );
  },
  {
    Provider: DigitalCardProvider,
    Content: DigitalCardContent,
  },
);

export default DigitalCard;
